// ============================================================================
// LoginScreen.jsx — ekrani i identifikimit (telefon + desktop)
//
// Ky komponent jep VETËM kornizën: sfondin, markën, numrat, tri kutitë dhe
// footer-in. Forma e identifikimit vjen si `children` — pra mbetet e njëjta
// formë e provuar te App.jsx (`renderAuthFormBody`), pa u dyfishuar. Kështu
// pamja ndryshon lirisht, ndërsa logjika e hyrjes s'preket fare.
//
// Pamja vjen nga baza (`rpc_get_login_settings`): varianti i zgjedhur, ngjyrat
// e tij, tekstet dhe numrat që i shkruan admini. Nëse baza s'përgjigjet,
// përdoret varianti "ember" i integruar — ekrani s'mbetet kurrë bosh.
//
// Sfondet janë vizatime CSS/SVG, jo fotografi: pa shkarkime, pa varësi, dhe
// shkallëzohen pa u turbulluar në çdo ekran.
// ============================================================================

import React from "react";
import { Capacitor } from "@capacitor/core";
import { SAFE_TOP, SAFE_BOTTOM } from "./zonaESigurt"; // kapaku 64/48 — shih shënimin atje

// Varianti rezervë — përdoret vetëm nëse baza s'ka përgjigjur ende DHE s'ka as
// një pamje të ruajtur nga hera e kaluar (shih `pef.login.last` te App.jsx).
//
// PSE U BË NEUTRAL, 20 gusht 2026. Deri sot ky ishte varianti "ember": i kuq i
// fortë, me shkëlqim të kuq. Pronari e pa duke u shfaqur shpesh në rifreskim
// dhe pyeti me të drejtë ku ruhej — sepse ai ekran nuk ishte ai që kishte
// zgjedhur ai. Nuk ruhej askund: ishte kjo kostante, e pikturuar për gjysmë
// sekonde derisa vinte përgjigjja e bazës.
//
// Një variant rezervë s'duhet të jetë kurrë deklaratë vizuale. Ai është ajo që
// shihet kur s'dimë ende çfarë të shfaqim, ndaj duhet të mos bjerë në sy: e errët
// dhe e qetë, pa asnjë ngjyrë që të kundërshtojë atë që vjen pas një çasti. I
// kuqi ishte pikërisht e kundërta — sa më i fortë, aq më i dukshëm ndërrimi.
//
// Nëse dikush e do ember-in, e zgjedh te Admin Center. Aty është zgjedhje; këtu
// ishte aksident.
export const FALLBACK_LOGIN = {
  art: "none",
  mode: "dark",
  tokens: {
    bg: "#14120E", bg2: "#1C1915", glow: "#2A2620", ink: "#EDE7DC", inkSoft: "#A79E90",
    card: "#1B1814", cardBorder: "#2E2921", accent: "#B8935A", accentInk: "#1A1712", tagline: "#C9BCA6",
  },
  title: "Pema e Familjes",
  tagline: "Ruaj historinë. Lidh brezat.",
  subtitle: "Fotografi, kujtime, histori dhe njerëz që nuk duhet të harrohen.",
  art_image: "", art_image_desktop: "", scrim: 30, fade: 18,
  show_stats: false, stats: [],
  show_features: false, features: [],
  footer: "",
};

// ---------------------------------------------------------------------------
// Ikonat — vija të holla, të njëjtin stil me pjesën tjetër të aplikacionit.
// ---------------------------------------------------------------------------
function Ico({ name, size = 20, color = "currentColor" }) {
  const p = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "users": return <svg {...p}><circle cx="9" cy="8" r="3.2" /><path d="M3 20c0-3.5 2.7-6 6-6s6 2.5 6 6" /><circle cx="17" cy="9" r="2.6" /><path d="M15.5 14.3c2.4.4 4.5 2.4 4.5 5.7" /></svg>;
    case "tree": return <svg {...p}><path d="M12 21v-6" /><circle cx="12" cy="5" r="2.4" /><circle cx="6.5" cy="11" r="2.1" /><circle cx="17.5" cy="11" r="2.1" /><path d="M12 7.4v3M10.4 9.8L8 10.4M13.6 9.8l2.4.6M8.2 12.6 12 15l3.8-2.4" /></svg>;
    case "heart": return <svg {...p}><path d="M12 20s-7-4.4-7-9.2A3.8 3.8 0 0 1 12 8a3.8 3.8 0 0 1 7 2.8C19 15.6 12 20 12 20z" /></svg>;
    case "shield": return <svg {...p}><path d="M12 3.5 19 6v5.5c0 4.3-2.9 7.4-7 8.9-4.1-1.5-7-4.6-7-8.9V6z" /><path d="M9.2 12.2l2 2 3.6-3.9" /></svg>;
    case "drop": return <svg {...p}><path d="M12 3.5s5 5.4 5 9a5 5 0 0 1-10 0c0-3.6 5-9 5-9z" /></svg>;
    case "star": return <svg {...p}><path d="m12 4 2.3 4.9 5.2.7-3.8 3.7.9 5.3-4.6-2.5-4.6 2.5.9-5.3L4.5 9.6l5.2-.7z" /></svg>;
    default: return <svg {...p}><circle cx="12" cy="12" r="8" /></svg>;
  }
}

// ---------------------------------------------------------------------------
// Stema — pema në rreth, e njëjta formë si te mockup-i.
// ---------------------------------------------------------------------------
function Crest({ size = 92, tk }) {
  // Vizatimi është GEOMETRIA E VËRTETË e ikonës së platformës (public/icon.svg),
  // jo një përafrim. Ndryshimi i vetëm: katrori i rrumbullakosur zëvendësohet me
  // rreth (siç është te mockup-i), dhe ngjyrat lidhen me variantin — e kuqja te
  // `accent`, e bardha te `accentInk`.
  const R = tk.accent, W = tk.accentInk;
  const L = (x1, y1, x2, y2, w) => <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={W} strokeWidth={w} strokeLinecap="round" />;
  return (
    <div className="lg-crest" style={{ position: "relative", borderRadius: "50%", boxShadow: `0 0 52px 8px ${tk.glow}55, 0 12px 32px rgba(0,0,0,.38)` }}>
      <svg viewBox="0 0 100 100" width="100%" height="100%" style={{ display: "block", borderRadius: "50%" }}>
        <circle cx="50" cy="50" r="50" fill={R} />
        <g transform="translate(8,3) scale(0.84)">
          <circle cx="50" cy="55" r="44" fill="none" stroke={W} strokeWidth="2" opacity="0.35" />
          <path d="M46,97 Q54,75 46,55" stroke={W} strokeWidth="4" fill="none" strokeLinecap="round" />
          <path d="M54,97 Q46,75 54,55" stroke={W} strokeWidth="4" fill="none" strokeLinecap="round" />
          {L(48, 94, 34, 99, 3)}
          {L(52, 94, 66, 99, 3)}
          {L(50, 50, 58, 28, 3.5)}
          {L(58, 28, 50, 13, 3)}
          {L(48, 74, 24, 54, 3.5)}
          {L(24, 54, 12, 38, 3)}
          {L(52, 74, 76, 54, 3.5)}
          {L(76, 54, 88, 38, 3)}
          {L(50, 62, 30, 42, 3.5)}
          {L(30, 42, 16, 26, 3)}
          {L(50, 62, 70, 42, 3.5)}
          {L(70, 42, 84, 26, 3)}
          {L(50, 52, 40, 32, 2.8)}
          {L(40, 32, 38, 16, 2.6)}
          {L(50, 52, 60, 32, 2.8)}
          {L(60, 32, 62, 16, 2.6)}
          <circle cx="50" cy="13" r="6" fill={W} />
          <circle cx="12" cy="38" r="6.5" fill={W} />
          <circle cx="88" cy="38" r="6.5" fill={W} />
          <circle cx="16" cy="26" r="6" fill={W} />
          <circle cx="84" cy="26" r="6" fill={W} />
          <circle cx="38" cy="16" r="5" fill={W} />
          <circle cx="62" cy="16" r="5" fill={W} />
          <circle cx="50" cy="13" r="2.4" fill={R} />
          <circle cx="12" cy="38" r="2.4" fill={R} />
          <circle cx="88" cy="38" r="2.4" fill={R} />
        </g>
      </svg>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Gjenerues pemësh — degëzim rekursiv, deterministik (i njëjti çdo herë, që
// pamja të mos "kërcejë" mes rifreskimeve). Kjo është ajo që i jep pemës
// dendësinë e vërtetë; me pak vija të shkruara me dorë del gjithnjë e varfër.
// ---------------------------------------------------------------------------
function lcg(seed) { let s = seed; return () => (s = (s * 1664525 + 1013904223) % 4294967296) / 4294967296; }

function growTree({ seed = 7, x = 150, y = 300, len = 62, angle = -90, width = 7, depth = 8, spread = 26, taper = 0.76 }) {
  const rnd = lcg(seed);
  const segs = [];
  const tips = [];
  (function branch(x0, y0, l, a, w, d) {
    if (d === 0 || l < 2) { tips.push([x0, y0, w]); return; }
    const rad = (a * Math.PI) / 180;
    const x1 = x0 + Math.cos(rad) * l;
    const y1 = y0 + Math.sin(rad) * l;
    segs.push([x0, y0, x1, y1, w]);
    const n = d > 6 ? 2 : rnd() > 0.72 ? 3 : 2;      // ndonjëherë tri degë — pa simetri mekanike
    for (let i = 0; i < n; i++) {
      const off = (i - (n - 1) / 2) * spread + (rnd() - 0.5) * spread * 0.75;
      branch(x1, y1, l * (0.7 + rnd() * 0.16), a + off, w * taper, d - 1);
    }
  })(x, y, len, angle, width, depth);
  return { segs, tips };
}

// Kokrrizimi — i njëjti efekt si te ilustrimet: zhurmë e imët mbi gjithçka.
function Grain({ opacity = 0.055, freq = 0.9 }) {
  return (
    <svg className="lg-grain" aria-hidden="true">
      <filter id="lg-noise">
        <feTurbulence type="fractalNoise" baseFrequency={freq} numOctaves="3" stitchTiles="stitch" />
        <feColorMatrix type="saturate" values="0" />
      </filter>
      <rect width="100%" height="100%" filter="url(#lg-noise)" opacity={opacity} />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Sfondet — një për çdo variant. Të gjitha vizatime, jo fotografi.
// ---------------------------------------------------------------------------
function Art({ art, tk, image, imageDesktop, scrim = 30, fade = 18, pamja, korniza }) {
  const base = { position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" };

  // FOTO — kur varianti ka një ilustrim, ai përdoret ashtu siç është. Vizatimi
  // vektorial mbetet vetëm rezervë (dhe ndez kur fotoja mungon a s'ngarkohet).
  // Fotoja e zgjedhur sipas gjerësisë — dhe një provë ngarkimi. Nëse skedari
  // mungon (p.sh. s'është bërë ende deploy), NUK mbetemi me një ngjyrë të
  // sheshtë: biem te sfondi i vizatuar, që ekrani të mos duket i zbrazët.
  const wide = typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(min-width:900px)").matches : false;
  const chosen = (wide ? (imageDesktop || image) : (image || imageDesktop)) || "";
  const [imgOk, setImgOk] = React.useState(chosen ? null : false);
  React.useEffect(() => {
    if (!chosen) { setImgOk(false); return; }
    let alive = true;
    const probe = new Image();
    probe.onload = () => { if (alive) setImgOk(true); };
    probe.onerror = () => {
      if (!alive) return;
      console.warn("[login] sfondi s'u ngarkua:", chosen, "— po përdor vizatimin");
      setImgOk(false);
    };
    probe.src = chosen;
    return () => { alive = false; };
  }, [chosen]);

  // Rezerva kur varianti ka foto por ajo s'u ngarkua: VETËM sfond i qetë në
  // ngjyrat e temës. Asnjë vizatim — një pemë e vizatuar do t'i zinte vendin
  // ilustrimit të vërtetë dhe do të dukej sikur ashtu është menduar dizajni.
  if (chosen && imgOk === false) {
    return (
      <div style={base}>
        <div style={{ position: "absolute", inset: 0, background: `radial-gradient(90% 65% at 50% 8%, ${tk.bg2}, ${tk.bg} 72%)` }} />
      </div>
    );
  }

  if (chosen && imgOk !== false) {
    const m = image || imageDesktop;
    const d = imageDesktop || image;
    return (
      <div style={base} className={korniza ? "lg-aside-bg" : undefined}>
        <div style={{ position: "absolute", inset: 0, background: tk.bg }} />
        {/* ⚠ 28 GUSHT 2026 — më parë të DYJA shtresat rrinin gjithmonë te faqja,
            dhe zgjedhjen e bënte vetëm `display:none` i media query-t. Te
            shfletuesi kjo mjafton. Brenda WKWebView-it të iOS-it nuk ia vlen
            të varesh nga ajo: fotot e desktopit janë 3840×2160, dhe një e
            tillë kushton ~32 MB kujtese sapo dekodohet — mbi një aparat ku
            gjithë procesi i faqes ka shumë më pak se një kompjuter.
            Tani vizatohet VETËM shtresa që i takon gjerësisë reale. */}
        {/* 3 shtator 2026 — NGA `background-image` TE `<img>`.
            Arsyeja: pronari kërkoi zmadhim dhe zhvendosje të ilustrimit nga
            Admin Center, dhe ato dy me `background-size`/`background-position`
            nuk shprehen dot bashkë në mënyrë të parashikueshme — «contain plus
            20%» s'ekziston si vlerë CSS. Me një `<img>` që ka `object-fit`,
            zmadhimi dhe zhvendosja bëhen `transform`, pra numra që kanë të
            njëjtin kuptim në çdo ekran: 100 = si e vendos vetë `fit`, 120 = një
            e pestë më e madhe, dhe zhvendosja matet me përqindje të vetë
            kornizës. Kjo është arsyeja pse pozicioni i ruajtur duket njësoj te
            telefoni dhe te kompjuteri — nuk varet nga pikselat e pajisjes. */}
        {/* ══ KORNIZA ═══════════════════════════════════════════════════════
            3 shtator 2026 · «foto dy, kur po e rrisi dritaren, po deformohet».
            Kishte të drejtë, dhe shkaku ishte i imi.

            Ilustrimi shtrihej sa krejt dritarja, kurse titulli dhe kartela
            rrinë brenda një blloku të qendërzuar `min(1180px, 94vw)`. Deri sa
            dritarja është më e ngushtë se ai bllok, të dyja rriten bashkë dhe
            kompozimi qëndron. Sapo dritarja e kalon, blloku ndalet — ilustrimi
            jo. Pema i largohet kartelës, dhe te 2000px i hyn nën të.

            Ka edhe një të dytë, më e fshehtë: `contain` ndërron vetë se cila
            anë e kufizon foton. Te 1271×780 kufizon gjerësia (foto 1271 e gjerë,
            me shirit bosh sipër e poshtë); te 2000×780 kufizon lartësia — dhe
            atëherë fotoja bëhet 1387 e gjerë dhe QENDËRZOHET, pra shtyhet 306px
            djathtas dhe rritet ~9%. Ky ishte kërcimi që pa.

            Zgjidhja: ilustrimi merr SAKTËSISHT të njëjtën kornizë si përmbajtja
            — i njëjti `min(1180px, 94vw)`, i njëjti qendërzim — dhe brenda saj
            matet vetëm nga gjerësia, i ankoruar poshtë. Pra as `contain` s'ka
            më ç'të ndërrojë: pema është gjithmonë po aq e gjerë sa blloku dhe
            gjithmonë me rrënjë mbi brez. Ngjyra e sfondit vazhdon deri në skaj,
            dhe meqë është pikërisht kremi i pikturës, kufiri nuk shihet.

            Vlen vetëm për rreshtimin «aside» (Heirloom). Tetë temat e para janë
            tekstura që duhet të mbulojnë krejt dritaren — ato s'i prek. */}
        <div className="lg-bgframe">
          <img
            className={wide ? "lg-bg-d" : "lg-bg-m"}
            src={wide ? d : m}
            alt=""
            aria-hidden="true"
            draggable="false"
            style={{
              transform: `translate(${pamja.x}%, ${pamja.y}%) scale(${pamja.zoom / 100})`,
              display: "block",
            }}
          />
        </div>
        <div style={{ position: "absolute", inset: 0, background: tk.bg, opacity: Math.max(0, Math.min(100, scrim)) / 100 }} />
        {/* Kokrrizim i lehtë — e afron me teksturën e ilustrimit dhe thyen
            brezat e kompresimit në zonat e errëta.
            ⚠ 28 GUSHT 2026 — jo brenda app-it native. Ky është një filtër
            `feTurbulence` mbi GJITHË ekranin, me `mix-blend-mode: overlay`:
            WebKit-i detyrohet ta vizatojë faqen në një fletë të veçantë dhe
            ta mbajë atë në kujtesë, në rezolucionin e plotë të ekranit. Në
            desktop s'ndihet; te telefoni është nga gjërat më të shtrenjta që
            mund t'i vësh një faqeje. Humbet vetëm një teksturë e imët që në
            ekran të vogël pothuajse nuk shihet. */}
        {!Capacitor.isNativePlatform() && <Grain opacity={0.045} freq={0.85} />}
        {fade > 0 && (
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: `${fade}%`,
                        background: `linear-gradient(to top, ${tk.bg} 0%, transparent 100%)`, opacity: 0.8 }} />
        )}
      </div>
    );
  }

  // Pemë e vërtetë, jo njollë: trung i hollë, degë, dhe kurorë nga rrathë që
  // përsërisin stemën. Mbahet e vogël dhe e ulët — atmosferë, jo ilustrim.
  const treeSilhouette = (opacity, fill) => (
    <svg className="lg-tree" viewBox="0 0 240 200" preserveAspectRatio="xMidYMax meet" style={{ opacity }}>
      <g fill="none" stroke={fill} strokeWidth="3.2" strokeLinecap="round">
        <path d="M120 200v-86" />
        <path d="M120 132 92 108M120 132l28-24M120 156 98 140M120 156l22-16M120 110l-16-16M120 110l16-16" />
        <path d="M104 200c2-14 8-22 16-26M136 200c-2-14-8-22-16-26" strokeWidth="2.2" opacity=".7" />
      </g>
      <g fill={fill}>
        {[[120, 74, 17], [92, 90, 13], [148, 90, 13], [74, 106, 10], [166, 106, 10],
          [104, 62, 10], [136, 62, 10], [120, 96, 11]].map(([cx, cy, r], i) => (
          <circle key={i} cx={cx} cy={cy} r={r} />
        ))}
      </g>
    </svg>
  );


  // Skena e variantit "ember" — sipas kadrit të dërguar nga përdoruesi.
  // Shtresa të ndara që asnjë të mos shtrembërohet kur ndryshon gjerësia.
  const emberScene = (tk) => {
    const g = tk.glow;
    // Pema-fantazmë: degëzim i gjerë, me ovale në majat e degëve.
    const ghost = growTree({ seed: 21, x: 150, y: 296, len: 54, width: 7.5, depth: 7, spread: 30 });
    // Pema e vogël mbi kodër: e dendur, e errët, si siluetë e vërtetë.
    const small = growTree({ seed: 5, x: 60, y: 116, len: 22, width: 3.4, depth: 7, spread: 32, taper: 0.74 });
    return (
      <>
        <svg className="lg-ghost" viewBox="0 0 300 300" preserveAspectRatio="xMidYMax meet">
          <g opacity="0.085">
            {ghost.segs.map(([x1, y1, x2, y2, w], i) => (
              <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={g} strokeWidth={Math.max(w, 0.8)} strokeLinecap="round" />
            ))}
            {ghost.tips.filter((_, i) => i % 3 === 0).map(([cx, cy], i) => (
              <ellipse key={i} cx={cx} cy={cy} rx="10" ry="13.5" fill={g} opacity="0.5" />
            ))}
          </g>
        </svg>

        <svg className="lg-land" viewBox="0 0 300 100" preserveAspectRatio="none">
          <defs>
            <radialGradient id="lg-horizon" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={g} stopOpacity="0.95" />
              <stop offset="40%" stopColor={g} stopOpacity="0.30" />
              <stop offset="100%" stopColor={g} stopOpacity="0" />
            </radialGradient>
          </defs>
          <ellipse cx="170" cy="54" rx="112" ry="42" fill="url(#lg-horizon)" />
          <path d="M0 100V64c30-3 52-13 78-21 24-8 44-3 68 6 28 11 72 18 154 9v42z" fill="#000" opacity="0.8" />
        </svg>

        <svg className="lg-smalltree" viewBox="0 0 120 130" preserveAspectRatio="xMidYMax meet">
          <g opacity="0.95">
            {small.segs.map(([x1, y1, x2, y2, w], i) => (
              <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#000" strokeWidth={Math.max(w, 0.7)} strokeLinecap="round" />
            ))}
            {small.tips.map(([cx, cy], i) => (
              <circle key={i} cx={cx} cy={cy} r={2.6} fill="#000" />
            ))}
          </g>
        </svg>
      </>
    );
  };

  const hills = (fill, opacity) => (
    <svg viewBox="0 0 400 120" preserveAspectRatio="none" style={{ position: "absolute", left: 0, right: 0, bottom: 0, width: "100%", height: 130, opacity }}>
      <path fill={fill} d="M0 78c56-26 96 10 148-4 46-12 74-30 118-18 34 9 82 40 134 22V120H0z" />
    </svg>
  );

  switch (art) {
    case "paper":
      return (
        <div style={base}>
          <div style={{ position: "absolute", inset: 0, background: `radial-gradient(120% 80% at 50% 0%, ${tk.bg}, ${tk.bg2})` }} />
          {hills(tk.glow, 0.5)}
          {treeSilhouette(0.14, tk.glow)}
        </div>
      );
    case "oak":
      return (
        <div style={base}>
          <div style={{ position: "absolute", inset: 0, background: `radial-gradient(90% 70% at 50% 12%, ${tk.bg2}, ${tk.bg} 70%)` }} />
          <div style={{ position: "absolute", inset: 0, background: `radial-gradient(45% 30% at 50% 22%, ${tk.glow}44, transparent 70%)` }} />
          {treeSilhouette(0.42, "#000")}
          {hills("#000", 0.6)}
        </div>
      );
    case "crimson":
      return (
        <div style={base}>
          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(160deg, ${tk.bg2} 0%, ${tk.bg} 78%)` }} />
          <svg viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.22 }}>
            {[...Array(26)].map((_, i) => {
              const x = (i * 79) % 400, y = (i * 137) % 400;
              return <g key={i} stroke={tk.glow} strokeWidth="0.7" fill={tk.glow}>
                <circle cx={x} cy={y} r="2.2" />
                <line x1={x} y1={y} x2={(x + 61) % 400} y2={(y + 47) % 400} />
              </g>;
            })}
          </svg>
        </div>
      );
    case "forest":
      return (
        <div style={base}>
          <div style={{ position: "absolute", inset: 0, background: `radial-gradient(110% 80% at 20% 0%, ${tk.bg2}, ${tk.bg} 65%)` }} />
          <svg viewBox="0 0 200 200" style={{ position: "absolute", top: -10, right: -20, width: 240, height: 240, opacity: 0.35 }}>
            {[...Array(7)].map((_, i) => (
              <ellipse key={i} cx={60 + i * 18} cy={40 + (i % 3) * 26} rx="26" ry="11"
                transform={`rotate(${-35 + i * 14} ${60 + i * 18} ${40 + (i % 3) * 26})`} fill={tk.glow} />
            ))}
          </svg>
          {treeSilhouette(0.16, tk.glow)}
        </div>
      );
    case "night":
      return (
        <div style={base}>
          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(180deg, ${tk.bg} 0%, ${tk.bg2} 100%)` }} />
          <svg viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
            {[...Array(60)].map((_, i) => {
              const x = (i * 53) % 400, y = (i * 97) % 210;
              return <circle key={i} cx={x} cy={y} r={i % 7 === 0 ? 1.5 : 0.8} fill="#fff" opacity={i % 3 === 0 ? 0.75 : 0.35} />;
            })}
          </svg>
          {treeSilhouette(0.5, "#000")}
          {hills("#000", 0.7)}
        </div>
      );
    case "ember":
    // "none" — sfond i qetë, pa skenë. Nuk është variant për t'u zgjedhur; është
    // ajo që shihet për një çast derisa dihet cili variant duhet. Prandaj s'ka as
    // shkëlqim, as pemë, as kokrra: asgjë që të bjerë në sy kur zëvendësohet.
    case "none":
      return (
        <div style={base}>
          <div style={{ position: "absolute", inset: 0, background: tk.bg }} />
        </div>
      );
    default:
      return (
        <div style={base}>
          <div style={{ position: "absolute", inset: 0, background: `radial-gradient(85% 62% at 50% 6%, ${tk.bg2}, ${tk.bg} 70%)` }} />
          <div style={{ position: "absolute", inset: 0, background: `radial-gradient(34% 22% at 50% 14%, ${tk.glow}44, transparent 74%)` }} />
          {emberScene(tk)}
          <Grain />
        </div>
      );
  }
}

// ---------------------------------------------------------------------------
// ── TASTIERA QË MBYLLEJ PAS ÇDO SHKRONJE (31 gusht 2026) ───────────────────
//
// Pronari: «pas çdo shkronje humbet tastatura dhe duhet të klikosh prapë».
//
// Shkaku ishte këtu, jo te forma. `Brand`, `Stats`, `Features` dhe `Card` ishin
// deklaruar si KOMPONENTË brenda trupit të këtij funksioni. Çdo render u jep
// identitet të ri; React e lexon atë si lloj tjetër komponenti te po ai
// pozicion, pra çmonton gjithçka brenda dhe monton nga e para. `<input>`-et e
// email-it dhe fjalëkalimit rrinë brenda `Card` — pra vdisnin e rilindnin, dhe
// fokusi bashkë me to. Te iOS, humbja e fokusit e mbyll tastierën.
//
// Rrethi mbyllej vetë: një shkronjë → `authForm` ndryshon te App → App
// rirenderon LoginScreen → `Card` lind sërish → inputi çmontohet → tastiera bie.
//
// Tani janë ELEMENTE, jo komponentë (`const card = (...)` në vend të
// `const Card = () => (...)`). Një element s'është «lloj»: React e njeh nyjen e
// vjetër dhe e mban. E njëjta pamje, i njëjti kod — vetëm pa çmontim.
//
// RREGULLI: asnjë komponent i deklaruar brenda trupit të një komponenti tjetër.
// Nëse duhet ndarë, nxirre jashtë dhe kaloji çfarë i duhet si props.
export default function LoginScreen({ settings, children, tr = (x) => x }) {
  const s = { ...FALLBACK_LOGIN, ...(settings || {}) };

  // TEMË E VEÇANTË PËR TELEFONIN
  //
  // Baza kthen dy grupe: pa prapashtesë për desktopin, me `_m` për telefonin.
  // Zgjedhja bëhet me `matchMedia` e jo me media query, sepse ngjyrat shkojnë
  // në stile inline (`tk.card`, `tk.ink`) — pra duhet ta dijë JavaScript-i.
  // Dëgjuesi i ndryshimit e mban të saktë edhe kur rrotullohet telefoni ose
  // zgjerohet dritarja, pa u rifreskuar faqja.
  const DESKTOP_Q = "(min-width:900px)";
  const [isDesktop, setIsDesktop] = React.useState(
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(DESKTOP_Q).matches
  );
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(DESKTOP_Q);
    const on = () => setIsDesktop(mq.matches);
    on();
    if (mq.addEventListener) { mq.addEventListener("change", on); return () => mq.removeEventListener("change", on); }
    mq.addListener(on); return () => mq.removeListener(on);      // Safari i vjetër
  }, []);
  // Kur telefoni s'ka temë të vetën, bie te ajo e desktopit — pra sjellja e
  // vjetër mbetet e paprekur derisa dikush ta ndajë me dorë te Admin Center.
  const pick = (dk, mk) => (isDesktop ? s[dk] : (s[mk] !== undefined && s[mk] !== null ? s[mk] : s[dk]));
  const tk = { ...FALLBACK_LOGIN.tokens, ...(pick("tokens", "tokens_m") || {}) };
  // `fit` rri te tokenat e temës e jo te një kolonë e re, sepse tokenat kalojnë
  // tashmë të plotë nga baza te ky komponent — dhe redaktimi i ngjyrave te Admin
  // Center shkruan te `custom_tokens`, pra nuk e prek kurrë këtë çelës.
  const lgFit = tk.fit === "contain" ? "contain" : "cover";
  const lgPos = tk.fit === "contain" ? "center center" : "center bottom";
  // RRESHTIMI «aside» — ilustrimi majtas, teksti dhe kartela djathtas.
  //
  // Te tetë temat e para sfondi është teksturë: përmbajtja rri në mes dhe
  // teksturës s'i prishet asgjë. Një ILUSTRIM me kompozim (si Heirloom) e
  // kërkon të kundërtën — pema majtas, gjysma tjetër e lirë. Pa këtë, titulli
  // binte mbi trung dhe s'lexohej.
  //
  // Rri te tokenat për të njëjtën arsye si `fit`: kalojnë tashmë të plota nga
  // baza, dhe redaktimi i ngjyrave te Admin Center shkruan te `custom_tokens`.
  const lgAside = tk.layout === "aside" ? " lg-aside" : "";

  // ══════════════════════════════════════════════════════════════════════════
  // SHKRONJAT E TEMËS — 3 shtator 2026
  //
  // Pronari: «edhe fontin do doja… me mujt mi errësu, mi bërë më të trasha, më
  // të dukshme, edhe me ndryshu fontin me ja përshtat dizajnit. Ruaje
  // origjinalin, bëje edhe një tjetër për këtë template — unë vetëm e bëj
  // switch në Admin Center.»
  //
  // PSE KËSHTU DHE JO NJË FUSHË FONTI PËR KREJT EKRANIN
  //   Deri sot Playfair Display ishte i ngulitur për të tetë temat. Një fushë e
  //   vetme «fonti i ekranit të hyrjes» do t'i prekte të tëra: zgjedh shkronjat
  //   për Heirloom-in dhe i ndërron edhe te «Gaca», «Letra», «Korija». Këtu
  //   fonti i takon TEMËS, si ngjyrat — pra ndërrimi i temës e sjell bashkë me
  //   vete, dhe «switch-i në Admin Center» është saktësisht ai që kërkoi.
  //   Tetë temat e para s'e kanë çelësin fare, pra mbeten me Playfair. Origjinali
  //   ruhet duke mos e prekur.
  //
  // Rri te `tokens` për të njëjtën arsye si `fit` dhe `layout`: kalon i plotë
  // nga baza, dhe redaktuesi i ngjyrave shkruan te `custom_tokens` — pra askush
  // s'e prek pa dashje. Asnjë kolonë e re, asnjë ndryshim te dera.
  const FONTET = {
    playfair: {
      importi: "Playfair+Display:wght@500;600;700",
      titulli: "'Playfair Display', Georgia, serif",
      trupi:   "'Playfair Display', Georgia, serif",
    },
    // Shkronjat e vetë shabllonit që dërgoi: Cormorant Garamond për titujt,
    // Lora për tekstin. Cormorant-i është nga natyra i hollë — prandaj peshat
    // e rënda vijnë bashkë me të te importi, ndryshe do të dilte edhe më i
    // zbehtë se Playfair-i që po zëvendëson.
    heirloom: {
      importi: "Cormorant+Garamond:ital,wght@0,600;0,700;1,600&family=Lora:ital,wght@0,500;0,600;1,500;1,600",
      titulli: "'Cormorant Garamond', Georgia, serif",
      trupi:   "'Lora', Georgia, serif",
    },
  };
  const fontet = FONTET[tk.font] || FONTET.playfair;
  // Pesha dhe pjerrësia janë të temës gjithashtu: një font tjetër ka nevojë për
  // pesha të tjera që të duket njësoj i fortë. Mungesa = sjellja e sotme.
  const peshaTitullit = Number(tk.titleWeight) || 600;
  const peshaSloganit = Number(tk.taglineWeight) || 400;
  const sloganPjerret = tk.taglineItalic ? "italic" : "normal";

  const stats = Array.isArray(s.stats) ? s.stats : [];
  // Çelësi dhe madhësia e secilit tekst, nga Admin Center. Mungesa = i ndezur,
  // madhësi normale — pra sjellja e vjetër mbetet kur s'është prekur asgjë.
  const ts = s.text_style || {};
  // Dukshmëria e ndarë: `d` = desktop, `m` = telefon. Nëse mungon, biem te
  // çelësi i vjetër i vetëm, që sjellja të mos ndryshojë pa e prekur askush.
  const vis = s.visibility || {};
  const vd = (k, fb) => vis[k]?.d ?? (fb !== undefined ? fb : ts[k]?.show !== false);
  const vm = (k, fb) => vis[k]?.m ?? (fb !== undefined ? fb : ts[k]?.show !== false);
  const shown = (k, fb) => vd(k, fb) || vm(k, fb);            // a shfaqet diku
  // Klasa që e fsheh atje ku s'duhet — bëhet me media query, jo me JS, që të
  // punojë edhe kur përdoruesi rrotullon telefonin ose zgjeron dritaren.
  const only = (k, fb) => (vd(k, fb) && vm(k, fb) ? "" : vd(k, fb) ? " lg-only-d" : " lg-only-m");
  const scale = (k) => (Number(ts[k]?.scale) || 100) / 100;

  // MADHËSIA E DY SHIRITAVE TË POSHTËM — 3 shtator 2026, kërkesë e pronarit:
  // «a mundem keta dy shiritat poshtë mi zvogëlu pak se po marrin shumë hapësirë».
  //
  // U bënë dy gjëra, jo një. E PARA: bazat u ulën një herë e mirë (rrethi i
  // ikonës 38→32, numri 21→18, titulli i kutisë 11.5→11, dhe hapësirat rreth
  // tyre), sepse ishin vërtet të mëdha dhe asnjë temë s'i kërkonte ashtu.
  // E DYTA: mbi atë bazë u vu një përqindje për secilin shirit, që herën tjetër
  // rregullimi të mos kërkojë deploy. Pse përqindje e jo piksela — sepse i
  // njëjti numër duhet të thotë të njëjtën gjë në telefon dhe në desktop, ku
  // bazat janë të ndryshme.
  //
  // Rrinë te `text_style.stats.scale` dhe `text_style.features.scale`: e njëjta
  // jsonb ku jetojnë tashmë titulli, slogani dhe fundi, pra asnjë kolonë e re
  // dhe asnjë ndryshim te `rpc_platform_save_login_settings`.
  const scStats = scale("stats");
  const scFeat = scale("features");

  // ══════════════════════════════════════════════════════════════════════════
  // TRE ZHVENDOSJE TË NDARA: STEMA · FJALËT · KARTELA E HYRJES
  //
  // 3 shtator 2026 · kërkesë e pronarit: «stemën, fjalët edhe kartelën e loginit
  // duhet mi nda të tria vetmas, mi zhvendos lart poshtë».
  //
  // Deri sot ekzistonte një zhvendosje e vetme (`block`), që i ngrinte ose i
  // ulte të tria bashkë. Kjo mjaftonte kur sfondi ishte teksturë. Me një
  // ILUSTRIM të kompozuar nuk mjafton: stema mund të duhet pak mbi degët, fjalët
  // te boshllëku i pikturës, dhe kartela poshtë tyre — tri largësi që s'kanë
  // asnjë arsye të lëvizin së bashku.
  //
  // ⚠ ZBATIMI U NDËRRUA — 3 shtator 2026, mbrëmje.
  //
  // Versioni i parë e bëri me `margin-top`, sepse margjina merr pjesë në
  // faqosje dhe kështu maja mbetej e mbrojtur. Po ajo zgjedhje e humbte
  // pikërisht atë që kërkohej: blloku qendërzohet me margjina `auto`, pra kur
  // stemës i jepen −100px, blloku shkurtohet 100px dhe qendërzimi ia kthen
  // gjysmën — stema ngrihet vetëm 50, dhe fjalët e kartela ngrihen 50 bashkë me
  // të. Domethënë as nuk lëvizte aq sa thoshte numri, as nuk lëvizte VETËM ajo.
  // Kjo ishte ankesa: «do doja që të mund ta ngrija stemën edhe më lart».
  //
  // Tani është `position:relative` + `top`, që lëviz vetëm atë element dhe
  // saktësisht aq sa thotë numri; siblings-at nuk e ndiejnë fare.
  //
  // ÇMIMI, i thënë hapur: pozicionimi relativ nuk e ndalon dot elementin të
  // dalë mbi zonën e sigurt. Për BLLOKUN e tërë kjo do të ishte e papranueshme
  // (dhe prandaj `block` mbetet margjinë) — për një element të vetëm që e ngre
  // vetë me dorë duke parë rezultatin, është zgjedhje e tij. Kufiri ±240px është
  // aty sa të mos shkojë numri i gabuar deri në pafundësi.
  //
  // `block` NUK hiqet. Ai lëviz krejt bllokun; këto tri lëvizin pjesët brenda
  // tij. Kush e ka rregulluar dikur `block`-un, s'i prishet asgjë.
  const oset = (k) => {
    const n = Number(ts.oset?.[k + (isDesktop ? "_d" : "_m")]);
    return Number.isFinite(n) ? Math.max(-240, Math.min(240, n)) : 0;
  };
  const osStema = oset("stema"), osFjalet = oset("fjalet"), osKartela = oset("kartela");

  // PAMJA E ILUSTRIMIT dhe GJERËSIA E KARTELËS — nga Admin Center.
  //
  // Rrinë te `text_style`, e cila është jsonb e lirë dhe ruhet e plotë nga
  // `rpc_platform_save_login_settings`. Pra asnjë kolonë e re, asnjë ndryshim
  // te dera — i njëjti vend ku jeton tashmë ngritja e bllokut (`block`).
  //
  // Desktopi dhe telefoni kanë vlerat e veta (`_d` / `_m`), sepse i njëjti
  // zmadhim që rri bukur te një ekran i gjerë e nxjerr pemën jashtë te një i
  // ngushtë. Brenda një pajisjeje numri është i njëjti kudo — dhe kjo ishte
  // kërkesa: «pozicioni të mbetet i njëjtë në të gjitha pajisjet».
  const _num = (v, fb) => (Number.isFinite(Number(v)) ? Number(v) : fb);
  const artC = ts.art || {};
  const pamja = isDesktop
    ? { zoom: _num(artC.zoom_d, 100), x: _num(artC.x_d, 0), y: _num(artC.y_d, 0) }
    : { zoom: _num(artC.zoom_m, 100), x: _num(artC.x_m, 0), y: _num(artC.y_m, 0) };
  const kartelaGjeresi = isDesktop
    ? _num(ts.card?.w_d, 400)
    : _num(ts.card?.w_m, 400);

  // Ngritja/ulja e bllokut qendror (stema, titujt, karta e hyrjes), në piksela:
  // negative = lart, pozitive = poshtë. Desktopi dhe telefoni kanë vlerën e vet.
  // Zbatohet si hapësirë (padding), jo si `transform` — shih shënimin e gjatë
  // poshtë për arsyen.
  const shift = (k) => {
    const n = Number(ts.block?.[k]);
    return Number.isFinite(n) ? Math.max(-160, Math.min(160, n)) : 0;
  };
  const features = Array.isArray(s.features) ? s.features : [];

  // ══════════════════════════════════════════════════════════════════════════
  // QENDËRZIMI QË NUK PRET KURRË MAJËN
  //
  // 31 gusht 2026 — rishkrim. Këtu qëndronte një mekanizëm që e mat me JavaScript
  // pozicionin e qendrës një herë dhe e ngul bllokun atje me `margin-top`
  // (`pin()`), me ResizeObserver, rimatje dhe përjashtime.
  //
  // PSE U HOQ: ai mekanizëm prodhoi katër defekte të ndryshme brenda dhjetë
  // ditësh — blloku lart pas daljes nga llogaria (20 gusht), stema nën shiritin
  // e sistemit, ekrani i zhvendosur kur kartela rritet, dhe përsëri pas daljes
  // më 31 gusht. Të gjitha kanë të njëjtin shkak: një matje e vetme, e marrë në
  // një çast që mund të jetë i gabuar, dhe pastaj e ngrirë. Sa herë që çasti
  // ishte i gabuar — një render i pambaruar, një font që s'kishte ardhur, një
  // kartelë që u rrit pas matjes — ngulja ishte e gabuar bashkë me të. Çdo arnë
  // shtonte një përjashtim, dhe çdo përjashtim hapte një rrugë të re.
  //
  // Kërkesa e vërtetë ishte gjithmonë e vogël: blloku rri në qendër kur ka vend,
  // dhe kur nuk ka, rritja shkon POSHTË — kurrë mbi majë. Kjo shprehet me dy
  // rreshta CSS, pa matur asgjë:
  //
  //     .lg-wrap  { justify-content: flex-start }
  //     .lg-split { margin-top: auto; margin-bottom: auto }
  //
  // Margjinat `auto` te një artikull flex e ndajnë hapësirën e lirë njësoj sipër
  // e poshtë — pra qendërzim. Kur hapësirë e lirë nuk ka më, ato bëhen zero
  // vetvetiu, blloku ngjitet te maja, dhe teprica del poshtë ku rrëshqitet.
  // Kjo është saktësisht sjellja e kërkuar, dhe browser-i e llogarit në çdo
  // kuadër — pa çast matjeje, pra pa çast të gabuar.
  //
  // NGRITJA NGA ADMIN CENTER bëhej me `transform: translateY`. Transform-i nuk
  // merr pjesë në faqosje: një vlerë negative e ngrinte bllokun mbi zonën e
  // sigurt pa e ditur askush, dhe asnjë rregull CSS s'e ndalonte dot. Tani e
  // njëjta ngritje shprehet si hapësirë: shtohet dyfishi i saj te ana e kundërt
  // e `padding`-ut, çka e zhvendos qendrën saktësisht aq. Meqë është faqosje,
  // margjinat `auto` e mbrojnë majën edhe këtu — kur s'ka vend, ngritja thjesht
  // s'ka ku të zbatohet, në vend që të presë stemën.
  // ══════════════════════════════════════════════════════════════════════════
  const shM = shift("m"), shD = shift("d");
  const ngritja = {
    "--lg-pt-m": Math.max(0, 2 * shM) + "px",
    "--lg-pb-m": Math.max(0, -2 * shM) + "px",
    "--lg-pt-d": Math.max(0, 2 * shD) + "px",
    "--lg-pb-d": Math.max(0, -2 * shD) + "px",
  };

  const brand = (
    <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
      {/* Stema fshihet e tëra me një çelës — deri sot mund të lëvizej lart e
          poshtë, po jo të hiqej. Te temat ku titulli është brenda vetë
          ilustrimit (p.sh. «Heirloom — me titull») ajo është përsëritje. */}
      {shown("crest", true) && (
        <span className={"lg-crestwrap" + only("crest", true)} style={{ position: "relative", top: osStema }}>
          <Crest tk={tk} />
        </span>
      )}
      {/* Fjalët janë një njësi e vetme — titulli, slogani dhe nënteksti lëvizin
          bashkë, sepse largësitë mes tyre janë çështje tipografie, jo pozicioni.
          Ajo që ndahet nga stema dhe nga kartela është KY grup. */}
      <div className="lg-words" style={{ position: "relative", top: osFjalet, display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
        {shown("title") && <h1 className={"lg-title" + only("title")} style={{ margin: 0, fontFamily: fontet.titulli, fontWeight: peshaTitullit, letterSpacing: "-.01em", color: tk.ink, lineHeight: 1.1 }}>
          {tr(s.title)}
        </h1>}
        {s.tagline && shown("tagline") && (
          <p className={"lg-tag" + only("tagline")} style={{ margin: 0, fontFamily: fontet.trupi, fontWeight: peshaSloganit, fontStyle: sloganPjerret, color: tk.tagline || tk.inkSoft }}>
            {tr(s.tagline)}
          </p>
        )}
        {s.subtitle && shown("subtitle") && (
          <p className={"lg-sub" + only("subtitle")} style={{ margin: 0, color: tk.inkSoft, lineHeight: 1.5, maxWidth: 340 }}>
            {tr(s.subtitle)}
          </p>
        )}
      </div>
    </div>
  );

  const stats_el = (!shown("stats", s.show_stats !== false) || stats.length === 0) ? null : (
    <div className="lg-statrow" style={{ display: "flex", justifyContent: "center", flexWrap: "wrap" }}>
      {stats.map((x, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: Math.round(8 * scStats) }}>
          <span style={{ width: Math.round(32 * scStats), height: Math.round(32 * scStats), borderRadius: "50%", display: "grid", placeItems: "center", border: `1px solid ${tk.cardBorder}`, color: tk.accent === "#FFFFFF" ? tk.ink : tk.accent }}>
            <Ico name={x.icon} size={Math.round(16 * scStats)} />
          </span>
          <span>
            <span style={{ display: "block", fontFamily: fontet.titulli, fontSize: 18 * scStats, color: tk.ink, lineHeight: 1.1 }}>{tr(String(x.value ?? ""))}</span>
            <span style={{ display: "block", fontSize: 10.5 * scStats, color: tk.inkSoft }}>{tr(x.label || "")}</span>
          </span>
        </div>
      ))}
    </div>
  );

  const features_el = (!shown("features", s.show_features !== false) || features.length === 0) ? null : (
    <div className={"lg-fgrid" + only("features", s.show_features !== false)}>
      {features.map((f, i) => (
        <div key={i} className="lg-fcell">
          <div className="lg-fhead">
            <span style={{ color: tk.accent === "#FFFFFF" ? tk.ink : tk.accent, flexShrink: 0, display: "flex" }}>
              <Ico name={f.icon} size={Math.round(15 * scFeat)} />
            </span>
            <span className="lg-ft" style={{ fontWeight: 600, color: tk.ink }}>{tr(f.title || "")}</span>
          </div>
          {f.body && shown("features_body", true) ? (
            <div className={"lg-fb" + only("features_body", true)} style={{ color: tk.inkSoft }}>
              {tr(f.body)}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );

  const card = (
    <div style={{
      background: tk.card, border: `1px solid ${tk.cardBorder}`, borderRadius: 16,
      padding: "14px 14px 12px", boxShadow: "0 24px 60px rgba(0,0,0,.35)", width: "100%", maxWidth: kartelaGjeresi,
      backdropFilter: "blur(14px) saturate(1.15)", WebkitBackdropFilter: "blur(14px) saturate(1.15)",
    }}>
      {children}
    </div>
  );

  return (
    <div style={{ position: "relative", flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", background: tk.bg, color: tk.ink,
                  display: "flex", flexDirection: "column",
                  "--sc-title": scale("title"), "--sc-tagline": scale("tagline"), "--sc-subtitle": scale("subtitle"),
                  "--sc-stats": scStats, "--sc-feat": scFeat,
                  "--lg-fit": lgFit, "--lg-pos": lgPos, "--lg-cardw": kartelaGjeresi + "px",
                  ...ngritja }}>
      {/* Kanavaca: sfondi dhe përmbajtja qendrore. Brezi rri JASHTË saj, pra
          fotoja mbaron pikërisht aty ku nis brezi — dhe pema, që qëndron në
          fund të ilustrimit, duket e plotë në vend që t'i pritet fundi. */}
      <div className={"lg-canvas" + lgAside}>
      <Art art={pick("art", "art_m")} tk={tk} image={s.art_image} imageDesktop={s.art_image_desktop}
           scrim={pick("scrim", "scrim_m")} fade={pick("fade", "fade_m")} pamja={pamja}
           korniza={lgAside !== ""} />

      {/* Rregullat responsive — një bllok i vetëm, që të mos duhet Tailwind
          config i ri. Nën 900px: kolonë e vetme (telefoni). Mbi: dy kolona. */}
      <style>{`
        @import url("https://fonts.googleapis.com/css2?family=${fontet.importi}&display=swap");
        /* ---- TELEFON: gjithçka në një ekran, pa scroll ----
           Numrat dhe tri kutitë janë përmbajtje shitëse; në telefon zënë gjysmën
           e faqes dhe e mbulojnë ilustrimin. I mbajmë vetëm në desktop, ku ka
           vend. Kështu pema duket dhe forma arrihet pa lëvizur asgjë. */
        .lg-canvas{position:relative;flex:1;min-height:0;display:flex;flex-direction:column}
        /* justify-content:flex-start + margjina auto te blloku = qendërzim
           kur ka vend, dhe ngjitje te maja kur s'ka. Kurrë prerje sipër. */
        .lg-wrap{position:relative;z-index:1;flex:1;display:flex;flex-direction:column;
                 align-items:center;justify-content:flex-start;gap:8px;
                 padding:calc(max(12px, ${SAFE_TOP}) + var(--lg-pt-m,0px)) 16px
                         calc(max(10px, ${SAFE_BOTTOM}) + var(--lg-pb-m,0px))}
        .lg-split{display:flex;flex-direction:column;align-items:center;gap:10px;width:100%;
                  margin-top:auto;margin-bottom:auto}
        .lg-side{display:flex;flex-direction:column;align-items:center;width:100%}
        .lg-card{width:100%;max-width:400px;display:flex;justify-content:center}
        .lg-crest{width:66px;height:66px}
        .lg-title{font-size:calc(24px * var(--sc-title,1));margin-top:9px}
        .lg-tag{font-size:calc(13.5px * var(--sc-tagline,1));margin-top:4px}
        .lg-sub{font-size:calc(12px * var(--sc-subtitle,1));margin-top:5px;line-height:1.4}
        /* Numrat dhe tri kutitë shfaqen edhe në telefon — çelësi te Admin
           Center thotë "shfaq", pra duhet të vlejë kudo. Në telefon vetëm
           ngjishen: kutitë në një kolonë, shkrimi më i vogël. */
        .lg-stats{display:block;width:100%}
        /* Mbështjellësi i stemës — "flex" e jo "inline", që klasa e fshehjes
           sipas pajisjes (lg-only-d / lg-only-m) të ketë çfarë të fshehë. */
        .lg-crestwrap{display:flex}
        /* Brezi i poshtëm */
        /* 3 shtator 2026 — BREZI U HOLLUA.
           Bazat e mëposhtme janë ulur një herë e mirë (rrethi 38→32, numri
           21→18, titulli 11.5→11, hapësirat rreth tyre), dhe mbi to shumëzon
           përqindja nga Admin Center: --sc-stats për numrat, --sc-feat për tri
           kutitë. Pra edhe pa e prekur askush brezi tashmë zë më pak, edhe
           rregullimi i radhës bëhet pa deploy.
           Hoqëm gjithashtu "min-height:32px" te koka e kutisë: ai numër ekzistonte
           që titujt njërreshtësh e dyrreshtësh të rreshtoheshin, po e blinte atë
           rreshtim me hapësirë bosh te çdo kuti njërreshtëshe — dhe ato janë të
           trija. */
        .lg-bar{flex-shrink:0;width:100%;padding:9px 12px calc(8px + ${SAFE_BOTTOM});
                backdrop-filter:blur(14px) saturate(1.1);-webkit-backdrop-filter:blur(14px) saturate(1.1)}
        .lg-fgrid{display:grid;grid-template-columns:repeat(3,1fr);max-width:1000px;margin:0 auto;width:100%;padding-top:calc(8px * var(--sc-feat,1));border-top:1px solid var(--lg-div,rgba(128,128,128,.28))}
        .lg-fcell{padding:0 8px;min-width:0}
        .lg-fcell + .lg-fcell{border-left:1px solid var(--lg-div,rgba(128,128,128,.28))}
        .lg-fhead{display:flex;align-items:center;gap:5px;min-height:0}
        .lg-ft{font-size:calc(11px * var(--sc-feat,1));line-height:1.2}
        .lg-fb{font-size:calc(10px * var(--sc-feat,1));line-height:1.3;margin-top:2px}
        .lg-statrow{gap:calc(16px * var(--sc-stats,1));margin-top:0;margin-bottom:calc(8px * var(--sc-stats,1))}
        .lg-foot{width:100%;max-width:960px;text-align:center}
        /* Ankorimi poshtë: pema qëndron në fund të ilustrimit, prandaj kur
           prerja "cover" duhet të heqë diçka, le të heqë majën e qiellit — jo
           rrënjët. Kështu pema mbaron saktësisht mbi brez dhe duket e plotë. */
        /* 3 shtator 2026 — SA E MADHE SHFAQET FOTOJA.
           Deri sot ishte gjithmonë "cover": fotoja mbush ekranin dhe teprica
           pritet. Për tetë temat e para kjo është e saktë — sfondet e tyre janë
           tekstura, dhe një teksturë s'ka «anë» që humbet. Por një ILUSTRIM ka:
           te tema Heirloom pronari pa degët e sipërme dhe rrënjët të prera.
           Prandaj tema mund të kërkojë "contain" — atëherë fotoja futet e tëra
           dhe hapësira që mbetet mbushet me ngjyrën e sfondit, pra as vihet re.
           Parazgjedhja mbetet "cover": asnjë temë ekzistuese nuk ndryshon.
           (Pa backtick-e këtu me qëllim: krejt ky bllok CSS jeton brenda një
           template literal, dhe një backtick i vetëm do ta mbyllte atë.) */
        /* Korniza e ilustrimit. E pandryshuar për tetë temat e para — inset:0,
           pra krejt dritarja, si gjithmonë. Vetëm «aside» e ngushton, dhe
           vetëm në desktop (shih bllokun e desktopit më poshtë). */
        .lg-bgframe{position:absolute;inset:0}
        .lg-bg-m,.lg-bg-d{position:absolute;inset:0;width:100%;height:100%;
                          object-fit:var(--lg-fit,cover);
                          object-position:var(--lg-pos,center bottom);
                          transform-origin:center center;pointer-events:none;user-select:none}
        .lg-bg-d{display:none}
        .lg-grain{position:absolute;inset:0;width:100%;height:100%;pointer-events:none;mix-blend-mode:overlay}
        .lg-ghost{position:absolute;left:50%;top:2%;transform:translateX(-50%);
                  width:min(620px,96%);height:56%}
        .lg-land{position:absolute;left:0;right:0;bottom:0;width:100%;height:22%}
        .lg-smalltree{position:absolute;left:16%;bottom:12%;width:78px;height:78px}
        .lg-tree{position:absolute;left:50%;transform:translateX(-50%);bottom:8%;
                 width:min(430px,80%);height:44%}

        /* Ekrane shumë të ulëta (telefona të vegjël, tastiera e hapur) */
        @media (max-height:760px){
          .lg-sub{display:none}
          .lg-crest{width:56px;height:56px}
          .lg-title{font-size:calc(21px * var(--sc-title,1));margin-top:6px}
          .lg-tag{font-size:calc(12.5px * var(--sc-tagline,1))}
        }

        /* ---- DESKTOP ---- */
        @media (min-width:900px){
          .lg-wrap{padding:calc(44px + var(--lg-pt-d,0px)) 40px calc(26px + var(--lg-pb-d,0px));gap:26px}
          .lg-split{flex-direction:row;align-items:center;justify-content:center;gap:80px;max-width:1100px}
          .lg-side{flex:1;max-width:470px}
          .lg-card{flex:0 0 auto;width:var(--lg-cardw,400px);max-width:100%}
          .lg-crest{width:132px;height:132px}
          .lg-title{font-size:calc(44px * var(--sc-title,1));margin-top:18px}
          .lg-tag{font-size:calc(19px * var(--sc-tagline,1));margin-top:10px}
          .lg-sub{font-size:calc(15px * var(--sc-subtitle,1));margin-top:12px;display:block}
          .lg-bar{padding:12px 40px 10px}
          .lg-fcell{padding:0 20px}
          .lg-fhead{min-height:0}
          .lg-ft{font-size:calc(13.5px * var(--sc-feat,1))}
          .lg-fb{font-size:calc(11.5px * var(--sc-feat,1));line-height:1.45;margin-top:3px}
          .lg-statrow{gap:calc(24px * var(--sc-stats,1));margin-bottom:calc(10px * var(--sc-stats,1))}
          .lg-bg-m{display:none}
          .lg-bg-d{display:block}
          .lg-ghost{top:0;width:min(760px,58%);height:74%}
          .lg-land{height:26%}
          .lg-smalltree{left:22%;bottom:15%;width:96px;height:96px}
          .lg-tree{left:32%;bottom:2%;width:min(520px,42%);height:64%}

          /* «aside»: një kolonë e vetme, djathtas. Titulli rri mbi kartelë e jo
             krah saj, sepse gjysma e majtë i takon ilustrimit.
             ⚠ 3 shtator 2026 — kolona ankorohet te BLLOKU, jo te dritarja.
             Versioni i parë e ankoronte kolonën te skaji i djathtë i dritares:
             te një ekran 2000px kartela fluturonte në cep, larg ilustrimit, dhe
             faqja mbetej e zbrazët në mes. Ilustrimi vetë qëndron te një brez i
             qendërzuar (ashtu e vendos "contain"), ndaj edhe përmbajtja duhet të
             qendërzohet me të njëjtën gjerësi — pastaj brenda saj shtyhet djathtas. */
          .lg-aside .lg-split{flex-direction:column;align-items:flex-end;gap:18px;
                              width:100%;max-width:min(1180px,94vw);
                              margin-left:auto;margin-right:auto}
          .lg-aside .lg-side{max-width:430px;text-align:center}
          .lg-aside .lg-card{flex:0 0 auto;width:100%;max-width:var(--lg-cardw,430px)}

          /* ILUSTRIMI MERR TË NJËJTËN KORNIZË SI PËRMBAJTJA.
             I njëjti numër si te ".lg-aside .lg-split" më sipër — dhe duhet të
             mbeten të njëjtë: nëse ndryshon njëri, pema dhe kartela nisin të
             largohen prapë kur zgjerohet dritarja. */
          .lg-aside-bg .lg-bgframe{left:50%;right:auto;top:0;bottom:0;
                                   width:min(1180px,94vw);transform:translateX(-50%)}
          /* Brenda kornizës matet VETËM nga gjerësia, e ankoruar poshtë. Kështu
             "contain" s'ka më ç'të ndërrojë kur dritarja bëhet e gjerë e e ulët:
             pema është gjithmonë sa korniza dhe rrënjët rrinë mbi brez. Kur
             dritarja është e ulët, humbet qielli sipër — jo rrënjët. */
          .lg-aside-bg .lg-bg-d{top:auto;bottom:0;height:auto;object-fit:fill;
                                transform-origin:center bottom}
        }

        /* Fshehja sipas pajisjes rri E FUNDIT me qëllim: ka të njëjtën peshë
           me rregullat e mësipërme, prandaj fiton vetëm nëse vjen pas tyre.
           E vendosur më lart, rregulli i desktopit do ta anulonte. */
        @media (max-width:899px){ .lg-only-d{display:none} }
        @media (min-width:900px){ .lg-only-m{display:none} }
      `}</style>

      <div className="lg-wrap">
        <div className="lg-split">
          {/* Kolona e markës — poshtë saj numrat, siç është te mockup-i */}
          <div className="lg-side">
            {brand}
            </div>
          <div className="lg-card" style={{ position: "relative", top: osKartela }}>
            {card}
          </div>
        </div>

      </div>
      </div>

      {/* Brezi i poshtëm — një shirit i vetëm që mbyll faqen: tri kutitë si
          tabelë me ndarës vertikalë, dhe poshtë tyre rreshti i të drejtave.
          Rri jashtë bllokut qendror, që të shtrihet skaj më skaj. */}
      {(shown("features", s.show_features !== false) || shown("stats", s.show_stats !== false) || (s.footer && shown("footer"))) && (
        <div className="lg-bar" style={{ background: tk.card, borderTop: `1px solid ${tk.cardBorder}`, "--lg-div": tk.cardBorder }}>
          <div className={"lg-stats" + only("stats", s.show_stats !== false)}>{stats_el}</div>
          {features_el}
          {s.footer && shown("footer") && (
            <p className={"lg-foot" + only("footer")} style={{ fontSize: 11 * scale("footer"), color: tk.inkSoft, opacity: 0.6, margin: "6px 0 0" }}>{tr(s.footer)}</p>
          )}
        </div>
      )}

      {/* RRETH1 · 3 shtator 2026 — LIDHJET PUBLIKE.
          Ekrani i hyrjes ishte e vetmja faqe që shihte një i panjohur, dhe nuk
          thoshte asgjë për platformën. Këto tri lidhje janë dera për jashtë:
          «Rreth platformës» te faqja publike që shpjegon çfarë është kjo gjë,
          dhe dy dokumentet ligjore. Rrinë JASHTË shiritit të mësipërm me
          qëllim — ai shirit fshihet kur admini i fik numrat dhe veçoritë, kurse
          këto duhet të jenë gjithmonë aty. */}
      {/* 3 shtator 2026 — më i hollë me kërkesë të pronarit: çdo piksel që merr
          ky shirit ia heq ilustrimit, dhe ai është arsyeja pse dikush e zgjodhi
          temën. Lidhjet mbeten të prekshme (11.5px, rresht i vetëm). */}
      <div style={{ textAlign: "center", padding: "10px 16px 14px" }}>
        <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.6, color: tk.inkSoft, opacity: 0.8 }}>
          <a href="/rreth" style={{ color: "inherit" }}>{tr("About the platform")}</a>
          <span style={{ opacity: 0.4, margin: "0 8px" }}>·</span>
          <a href="/privacy" style={{ color: "inherit" }}>{tr("Privacy Policy")}</a>
          <span style={{ opacity: 0.4, margin: "0 8px" }}>·</span>
          <a href="/terms" style={{ color: "inherit" }}>{tr("Terms of Use")}</a>
        </p>
      </div>
    </div>
  );
}
